import { theme } from '../../lib/theme';

/**
 * LoadingSpinner — branded loading indicator for Trovva.
 *
 * Variants:
 *   'page'    — full-height centered spinner for route-level loading
 *   'section' — compact spinner for card/panel sections
 *   'inline'  — tiny inline spinner for buttons or text
 *
 * Usage:
 *   <LoadingSpinner />                         // page-level
 *   <LoadingSpinner variant="section" />        // in a card
 *   <LoadingSpinner variant="inline" />         // in a button
 *   <LoadingSpinner label="Loading deals..." /> // with text
 */

const keyframesId = 'trovva-spinner-keyframes';

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById(keyframesId)) {
  const style = document.createElement('style');
  style.id = keyframesId;
  style.textContent = `
    @keyframes trovva-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes trovva-pulse-dot {
      0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
      40% { transform: scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

export default function LoadingSpinner({ variant = 'page', label, style: extraStyle }) {
  if (variant === 'inline') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        ...extraStyle,
      }}>
        <span style={{
          width: 14, height: 14,
          border: `2px solid ${theme.colors.border}`,
          borderTopColor: theme.colors.primary,
          borderRadius: '50%',
          animation: 'trovva-spin 0.8s linear infinite',
          display: 'inline-block',
        }} />
        {label && <span style={{ fontSize: 13, color: theme.colors.textLight }}>{label}</span>}
      </span>
    );
  }

  const isSection = variant === 'section';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: isSection ? '40px 20px' : '80px 20px',
      minHeight: isSection ? 200 : 400,
      ...extraStyle,
    }}>
      {/* Animated logo mark */}
      <div style={{
        position: 'relative',
        width: isSection ? 48 : 56,
        height: isSection ? 48 : 56,
        marginBottom: 16,
      }}>
        {/* Spinning ring */}
        <div style={{
          position: 'absolute', inset: 0,
          border: `3px solid ${theme.colors.border}`,
          borderTopColor: theme.colors.primary,
          borderRightColor: theme.colors.primaryLight,
          borderRadius: '50%',
          animation: 'trovva-spin 1s ease-in-out infinite',
        }} />
        {/* Center T mark */}
        <div style={{
          position: 'absolute',
          inset: isSection ? 8 : 10,
          borderRadius: 10,
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, color: 'white',
          fontSize: isSection ? 16 : 18,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
          T
        </div>
      </div>

      {/* Label */}
      {label && (
        <p style={{
          color: theme.colors.textLight,
          fontSize: isSection ? 13 : 14,
          fontWeight: 500, margin: 0,
        }}>
          {label}
        </p>
      )}

      {/* Animated dots */}
      {!label && (
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 6, height: 6,
              borderRadius: '50%',
              background: theme.colors.primary,
              animation: `trovva-pulse-dot 1.4s ease-in-out ${i * 0.16}s infinite`,
            }} />
          ))}
        </div>
      )}
    </div>
  );
}
